import api from './api';

/**
 * Login a user
 * @param {string} email 
 * @param {string} password 
 * @returns {Promise<object>} User data and token
 */
const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  
  // If login is successful, store user info (including token) in localStorage
  if (response.data) {
    localStorage.setItem('userInfo', JSON.stringify(response.data));
  }
  
  return response.data;
};


const signup = async (userData) => {
  const response = await api.post('/auth/signup', userData);

  // If signup is successful, automatically log them in by storing their info
  if (response.data) {
    localStorage.setItem('userInfo', JSON.stringify(response.data));
  }

  return response.data;
};

/**
 * Update user profile
 * @param {object} profileData - Fields to update (name, email, password, etc.)
 * @returns {Promise<object>} Updated user data
 */
const updateProfile = async (profileData) => {
  const response = await api.put('/auth/profile', profileData);
  
  // Update the stored user info with the new data, preserving the token
  if (response.data) {
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    const updatedUserInfo = { ...userInfo, ...response.data };
    localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
  }

  return response.data;
};

/**
 * Delete user profile
 * @returns {Promise<object>} Success message
 */
const deleteProfile = async () => {
  const response = await api.delete('/auth/profile');
  // After deleting, log the user out
  logout();
  return response.data;
};

/**
 * Sign out the user
 * NOTE: The backend /signout route is called, but the primary action
 * for the frontend is to remove the user info from localStorage.
 */
const logout = () => {
  localStorage.removeItem('userInfo');
};

/**
 * Request a password reset link
 * @param {string} email 
 * @returns {Promise<object>} Success message
 */
const forgotPassword = async (email) => {
  const response = await api.post('/auth/forgot-password', { email });
  return response.data;
};

/**
 * Reset password using a token
 * @param {string} token - The reset token from the URL/email link
 * @param {string} password - The new password
 * @returns {Promise<object>} Success message
 */
const resetPassword = async (token, password) => {
  const response = await api.put(`/auth/reset-password/${token}`, { password });
  return response.data;
};

/**
 * Change the password for a logged-in user
 * @param {string} oldPassword 
 * @param {string} newPassword 
 * @returns {Promise<object>} Success message
 */
const changePassword = async (oldPassword, newPassword) => {
  const response = await api.put('/auth/change-password', { oldPassword, newPassword });
  return response.data;
};


// Export all functions as a single service object
const authService = {
  login,
  signup,
  logout,
  updateProfile,
  deleteProfile,
  forgotPassword,
  resetPassword,
  changePassword,
};

export default authService;